import { Box, Button, Typography, useTheme } from "@mui/material";
import { differenceInCalendarDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Hexagon } from "../../../../components/ui";

// ----------------------------------------------------------------------

/** Dias do ciclo inferidos do nome do plano; null = ciclo desconhecido (sem barra) */
export function planCycleDays(planName: string | null | undefined): number | null {
  const name = (planName ?? "").toLowerCase();
  if (name.includes("anual")) return 365;
  if (name.includes("mensal") || name.includes("mês")) return 30;
  return null;
}

interface PlanCardProps {
  planType: "free" | "paid";
  planName: string | null;
  endDate?: string;
  onManage: () => void;
}

export default function PlanCard({ planType, planName, endDate, onManage }: PlanCardProps) {
  const theme = useTheme();
  const isFree = planType === "free";

  const end = endDate ? new Date(endDate) : null;
  const daysLeft = end && !Number.isNaN(end.getTime()) ? Math.max(0, differenceInCalendarDays(end, new Date())) : null;
  const cycle = planCycleDays(planName);
  const progress = daysLeft !== null && cycle ? Math.max(0, Math.min(1, daysLeft / cycle)) : null;
  const expiringSoon = daysLeft !== null && daysLeft < 7;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        background: theme.palette.brand.heroCard,
        border: `1px solid ${theme.palette.brand.heroCardBorder}`,
        borderRadius: "18px",
        p: "26px",
        color: theme.palette.common.white,
      }}
    >
      <Hexagon
        size={150}
        stroke={theme.palette.brand.creamStroke === "transparent" ? theme.palette.primary.main : theme.palette.brand.creamStroke}
        strokeWidth={2}
        sx={{ position: "absolute", right: -30, top: -20, opacity: 0.16 }}
      />

      <Box sx={{ position: "relative" }}>
        <Typography sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "1.2px", textTransform: "uppercase", opacity: 0.8 }}>
          Seu plano
        </Typography>
        <Typography sx={{ m: "6px 0 2px", fontFamily: theme.typography.fontFamilyDisplay, fontSize: 28 }}>
          {isFree ? "Gratuito" : planName}
        </Typography>

        {isFree ? (
          <Typography sx={{ fontSize: "13px", opacity: 0.7 }}>
            Desbloqueie consultas ilimitadas e todos os recursos.
          </Typography>
        ) : (
          end && (
            <Typography sx={{ fontSize: "13px", opacity: 0.7, color: expiringSoon ? "warning.light" : "inherit" }}>
              Renova em {format(end, "d 'de' MMMM", { locale: ptBR })}
            </Typography>
          )
        )}

        {!isFree && daysLeft !== null && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", m: "18px 0 14px" }}>
            {progress !== null && (
              <Box sx={{ flex: 1, height: 6, borderRadius: "100px", overflow: "hidden", backgroundColor: "rgba(255,255,255,0.2)" }}>
                <Box sx={{ width: `${Math.round(progress * 100)}%`, height: "100%", backgroundColor: theme.palette.brand.creamStroke === "transparent" ? theme.palette.primary.main : theme.palette.brand.creamStroke }} />
              </Box>
            )}
            <Typography sx={{ fontSize: "12px", opacity: 0.85 }}>{daysLeft} dias</Typography>
          </Box>
        )}

        <Button
          fullWidth
          onClick={onManage}
          sx={{
            mt: isFree ? "18px" : 0,
            height: 44,
            backgroundColor: "rgba(255,255,255,0.94)",
            color: "secondary.main",
            "&:hover": { backgroundColor: theme.palette.common.white },
          }}
        >
          {isFree ? "Conhecer planos" : "Gerenciar plano"}
        </Button>
      </Box>
    </Box>
  );
}
